<template>
  <div class="global-ledger-accounts-table">
    <!-- NEW SERVICE BUTTON AND SEARCH -->
    <b-row
      v-if="showSearch || showButton"
      class="mb-2 d-flex flex-wrap"
      :class="{
        'justify-content-between': showButton,
        'justify-content-start': !showButton,
      }"
    >
      <b-col v-if="showButton" cols="3" md="auto" class="mb-1 mb-md-0">
        <b-button
          variant="primary"
          block
          class="d-flex justify-content-center"
          @click.prevent="newAccount"
        >
          <feather-icon icon="PlusIcon" class="d-block d-md-none" />
          <span class="d-none d-md-inline">
            {{ $t("Afegeix compte comptable") }}
          </span>
        </b-button>
      </b-col>
      <b-col
        v-if="showSearch"
        cols="9"
        md="auto"
        class="mb-1 mb-md-0"
        :class="{
          'justify-content-between': showButton,
          'justify-content-start': !showButton,
        }"
      >
        <b-form-input
          id="filterInput"
          v-model="filter"
          type="search"
          :placeholder="`${$t('Cerca')}...`"
          :disabled="loading"
        />
      </b-col>
    </b-row>

    <!-- GLOBAL LEDGER ACCOUNTS TABLE -->
    <b-row>
      <b-col md="12">
        <b-table
          striped
          borderless
          hover
          responsive
          class="position-relative"
          :busy="loading"
          :per-page="perPage"
          :current-page="currentPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          sort-icon-left
          :items="accounts"
          show-empty
          :empty-text="$t('errors.emptyTable.title')"
          :empty-filtered-text="$t('errors.emptyTable.title')"
          :fields="fields"
          :filter="filter"
          @filtered="onFiltered"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle mr-2" />
              <strong>{{ $t("Carregant") }}...</strong>
            </div>
          </template>

          <!-- Column: name -->
          <template #cell(name)="data">
            {{ getGlobalLedgerAccountName(data.item.code) }}
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <div class="d-flex">
              <feather-icon
                icon="EditIcon"
                @click="editAccount(data.item.uuid)"
                class="mr-1 text-primary"
              />
              <feather-icon
                icon="TrashIcon"
                @click="confirmDeleteAccount(data.item.uuid)"
                class="text-danger"
              />
            </div>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <!-- PAGINATION -->
    <b-row
      v-show="!loading"
      v-if="accounts.length > pageOptions[0]"
      class="d-flex justify-content-between align-items-center"
    >
      <b-col
        cols="12"
        md="4"
        lg="3"
        class="mb-1 mb-md-0 d-flex justify-content-center justify-content-md-start"
      >
        <b-form-group
          id="per-page-input"
          :label="`${$t('Per pàgina')}`"
          label-cols="6"
          label-align="left"
          label-size="sm"
          label-for="sortBySelect"
          class="text-nowrap mr-1"
        >
          <b-form-select
            id="perPageSelect"
            v-model="perPage"
            size="sm"
            inline
            :options="pageOptions"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        md="4"
        lg="3"
        class="mb-1 mb-md-0 d-flex justify-content-center justify-content-md-end"
      >
        <div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BTable,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BFormInput,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import { getOtaName } from "@/utils/methods";

export default {
  components: {
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BFormInput,
    BButton,
    BSpinner,
  },
  props: {
    accounts: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    showButton: {
      type: Boolean,
      default: () => false,
    },
    showSearch: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      isBusy: false,
      sortBy: "name",
      sortDesc: false,
      perPage: 10,
      pageOptions: [5, 10, 25, 50],
      totalRows: 1,
      currentPage: 1,
      filter: null,
      fields: [
        { key: "name", label: this.$t("Nom"), sortable: true },
        { key: "number", label: this.$t("Nº compte"), sortable: true },
        { key: "description", label: this.$t("Descripció"), sortable: false },
        { key: "actions", label: this.$t("Accions"), sortable: false },
      ],
    };
  },
  watch: {
    accounts(accounts) {
      this.totalRows = accounts.length;
      this.currentPage = 1;
    },
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.accounts.length;
  },
  methods: {
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    newAccount() {
      this.$emit("button-click");
    },
    editAccount(accountUuid) {
      this.$emit("edit-account-click", accountUuid);
    },
    confirmDeleteAccount(accountUuid) {
      this.$swal({
        title: "Eliminar compte",
        text: "Estàs a punt d'eliminar aquest compte comptable, estàs segur/a?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Elimina",
        cancelButtonText: "Cancel·la",
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) this.deleteAccount(accountUuid);
      });
    },
    deleteAccount(accountUuid) {
      this.$store.dispatch("app/setLoading", true);
      setTimeout(() => {
        this.$store
          .dispatch("ledgerAccounts/deleteAccount", accountUuid)
          .then(() => this.$emit("account-deleted"))
          .catch(() => {
            this.$emit("delete-account-error");
            // TODO: Log error in Sentry? Only if is not logged in the store
          })
          .finally(() => this.$store.dispatch("app/setLoading", false));
      }, 100);
    },
    getGlobalLedgerAccountName(code) {
      const accountNames = {
        VAT: "Compte d'IVA",
        VAT_SUBURANCE: "Compte d'IVA (subarrendament)",
        SALES: "Compte de Vendes",
        SALES_SUBURANCE: "Compte de Vendes (subarrendament)",
        BANK: "Compte de Bancs",
      };

      return accountNames[code] || getOtaName(code) || code;
    },
  },
};
</script>

<style lang="scss">
.global-ledger-accounts-table {
  #per-page-input__BV_label_ {
    margin-top: 5px;
  }
}
</style>
